import clsx from "clsx";

import { ApiError, errorMessage, isOffline } from "../lib/api";

export type ErrorStateProps = {
  error: unknown;
  /** Overrides the headline; the default is chosen from the error itself. */
  title?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
};

function headlineFor(error: unknown): string {
  if (isOffline(error)) return "We can't reach Foundling.";
  if (error instanceof ApiError) {
    if (error.status === 404) return "That page isn't in the book.";
    if (error.status === 403) return "That isn't yours to look at.";
    if (error.status === 429) return "Steady on — too many requests.";
    if (error.status >= 500) return "Foundling is having a moment.";
  }
  return "That didn't work.";
}

/**
 * Errors are specific and recoverable. Never "an error occurred": say what
 * happened, and offer the one action that might fix it.
 */
export function ErrorState({ error, title, onRetry, retryLabel = "Try again", className }: ErrorStateProps) {
  const detail = errorMessage(error);

  return (
    <section
      role="alert"
      className={clsx(
        "sheet grain relative mx-auto max-w-md border-l-2 border-l-rust px-5 py-6 text-center",
        className,
      )}
    >
      <p className="field-label text-rust">Trouble</p>
      <h2 className="mt-2 display-5 text-xl leading-tight text-ink">{title ?? headlineFor(error)}</h2>
      <p className="mx-auto mt-2 max-w-prose font-prose leading-relaxed text-ink-soft">{detail}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="touch mt-5 inline-flex items-center justify-center border border-ink bg-card px-4 py-2 font-label text-sm font-medium tracking-wide text-ink transition-colors hover:bg-paper-deep"
        >
          {retryLabel}
        </button>
      ) : null}
    </section>
  );
}

